import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Upload, FileText, ImagePlus, Plus, BadgeCheck, Clock, X, Save, Flame, Star, Sparkles, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GROUPS = ["controladores", "sondas", "bombas"];
const LANGS = ["es", "en", "de"];
const INTEREST_KEY = { info: "lead.interestInfo", dosaloggers: "lead.interestDosaloggers", ambas: "lead.interestBoth" };

const emptyTr = () => ({
  es: { name: "", description: "", specs: [] },
  en: { name: "", description: "", specs: [] },
  de: { name: "", description: "", specs: [] },
});

const normalizeTr = (i18n) => {
  const base = emptyTr();
  LANGS.forEach((l) => {
    const t = (i18n && i18n[l]) || {};
    base[l] = { name: t.name || "", description: t.description || "", specs: t.specs || [] };
  });
  return base;
};

function SpecEditor({ t, value, onChange }) {
  return (
    <div className="space-y-2">
      {value.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={s.label} onChange={(e) => onChange(value.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
            placeholder={t("adminProducts.specLabel")} className="h-9" />
          <Input value={s.value} onChange={(e) => onChange(value.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
            placeholder={t("adminProducts.specValue")} className="h-9" />
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <X className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { label: "", value: "" }])}>
        <Plus className="mr-1.5 h-4 w-4" /> {t("adminProducts.addSpec")}
      </Button>
    </div>
  );
}

function TranslationEditor({ t, tr, setTr, idPrefix }) {
  const upd = (lang, field, val) => setTr({ ...tr, [lang]: { ...tr[lang], [field]: val } });
  return (
    <Tabs defaultValue="es" className="w-full">
      <TabsList>
        {LANGS.map((l) => (
          <TabsTrigger key={l} value={l} data-testid={`${idPrefix}-lang-${l}`}>{l.toUpperCase()}</TabsTrigger>
        ))}
      </TabsList>
      {LANGS.map((l) => (
        <TabsContent key={l} value={l} className="space-y-3 pt-2">
          <div>
            <Label className="text-xs uppercase text-slate-500">{t("common.name")} {l === "es" && "*"}</Label>
            <Input value={tr[l].name} onChange={(e) => upd(l, "name", e.target.value)} className="mt-1" data-testid={`${idPrefix}-name-${l}`} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">{t("adminProducts.description")}</Label>
            <Textarea value={tr[l].description} onChange={(e) => upd(l, "description", e.target.value)} rows={2} className="mt-1" data-testid={`${idPrefix}-desc-${l}`} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">{t("adminProducts.specs")}</Label>
            <div className="mt-1.5"><SpecEditor t={t} value={tr[l].specs} onChange={(v) => upd(l, "specs", v)} /></div>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

const VID_LABELS = ["montaje", "mantenimiento", "demo", "otro"];

function VideoEditor({ t, value, onChange }) {
  const label = { montaje: t("adminProducts.vidMontaje"), mantenimiento: t("adminProducts.vidMantenimiento"), demo: t("adminProducts.vidDemo"), otro: t("adminProducts.vidOtro") };
  return (
    <div className="space-y-2">
      {value.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={v.url} onChange={(e) => onChange(value.map((x, j) => j === i ? { ...x, url: e.target.value } : x))}
            placeholder="https://youtu.be/…" className="h-9 flex-1" />
          <Select value={v.label} onValueChange={(val) => onChange(value.map((x, j) => j === i ? { ...x, label: val } : x))}>
            <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{VID_LABELS.map((l) => <SelectItem key={l} value={l}>{label[l]}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <X className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { url: "", label: "demo" }])}>
        <Plus className="mr-1.5 h-4 w-4" /> {t("adminProducts.addVideo")}
      </Button>
    </div>
  );
}


function ManualRow({ t, pid, m, onUpload, onDeleteFile, onDeleteManual, onSaveTitle }) {
  const refs = { es: useRef(), en: useRef(), de: useRef() };
  const [titles, setTitles] = useState(() => {
    const ti = m.title_i18n || {};
    return { es: ti.es || m.title || "", en: ti.en || "", de: ti.de || "" };
  });
  return (
    <div className="rounded border border-slate-100 p-2" data-testid={`admin-manual-${m.id}`}>
      <div className="flex items-center gap-2 text-sm">
        <FileText className="h-4 w-4 text-blue-600" />
        <span className="flex-1 truncate">{m.title}</span>
        <Button variant="ghost" size="sm" onClick={() => onDeleteManual(pid, m.id)} data-testid={`del-manual-${m.id}`}>
          <Trash2 className="h-3.5 w-3.5 text-red-400" />
        </Button>
      </div>
      <div className="mt-2">
        <Label className="text-[10px] uppercase text-slate-400">{t("adminProducts.manualTitles")}</Label>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {LANGS.map((lg) => (
            <div key={lg} className="flex items-center gap-1">
              <span className="text-[10px] font-semibold text-slate-400">{lg.toUpperCase()}</span>
              <Input value={titles[lg]} onChange={(e) => setTitles({ ...titles, [lg]: e.target.value })}
                className="h-8 w-32 text-xs" data-testid={`manual-title-${m.id}-${lg}`} />
            </div>
          ))}
          <Button type="button" variant="secondary" size="sm" onClick={() => onSaveTitle(pid, m.id, titles)} data-testid={`save-manual-title-${m.id}`}>
            <Save className="mr-1 h-3.5 w-3.5" /> {t("adminProducts.saveTitle")}
          </Button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {LANGS.map((lg) => {
          const has = m.filenames && m.filenames[lg];
          return (
            <div key={lg} className={`flex items-center gap-1.5 rounded border px-2 py-1 text-xs ${has ? "border-blue-200 bg-blue-50/50" : "border-slate-200"}`}>
              <span className="font-semibold text-slate-600">{lg.toUpperCase()}</span>
              {has ? (
                <>
                  <span className="max-w-[110px] truncate text-slate-500" title={m.filenames[lg]}>{m.filenames[lg]}</span>
                  <button type="button" onClick={() => refs[lg].current.click()} title={t("adminProducts.replaceLang")} data-testid={`manual-replace-${m.id}-${lg}`}>
                    <Upload className="h-3.5 w-3.5 text-slate-500 hover:text-blue-600" />
                  </button>
                  <button type="button" onClick={() => onDeleteFile(pid, m.id, lg)} data-testid={`manual-delfile-${m.id}-${lg}`}>
                    <X className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => refs[lg].current.click()} className="inline-flex items-center gap-1 text-slate-400 hover:text-blue-600" data-testid={`manual-upload-${m.id}-${lg}`}>
                  <Upload className="h-3.5 w-3.5" /> {t("adminProducts.uploadLang")}
                </button>
              )}
              <input ref={refs[lg]} type="file" accept="application/pdf" hidden
                onChange={(e) => { onUpload(pid, m.id, lg, e.target.files[0]); e.target.value = ""; }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [downloads, setDownloads] = useState(null);
  const [group, setGroup] = useState("controladores");
  const [featured, setFeatured] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [tr, setTr] = useState(emptyTr());
  const [reference, setReference] = useState("");
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, l, d] = await Promise.all([api.get("/products"), api.get("/leads"), api.get("/downloads")]);
    setProducts(p.data); setLeads(l.data); setDownloads(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cleanTr = (data) => {
    const out = {};
    LANGS.forEach((l) => {
      out[l] = { name: data[l].name, description: data[l].description,
                 specs: data[l].specs.filter((s) => s.label.trim() && s.value.trim()) };
    });
    return out;
  };

  const createProduct = async (e) => {
    e.preventDefault();
    if (!tr.es.name.trim()) { toast.error(t("adminProducts.manualNeed")); return; }
    setBusy(true);
    try {
      await api.post("/products", { group, featured, is_new: isNew, reference, videos: videos.filter((v) => v.url.trim()), i18n: cleanTr(tr) });
      setTr(emptyTr()); setFeatured(false); setIsNew(false); setReference(""); setVideos([]);
      toast.success(t("adminProducts.created"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success(t("adminProducts.deleted")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/products/${id}/image`, fd); toast.success(t("adminProducts.imageOk")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const deleteImage = async (pid, iid) => {
    try { await api.delete(`/products/${pid}/images/${iid}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const uploadManual = async (id, title, lang, file) => {
    if (!file || !title) { toast.error(t("adminProducts.manualNeed")); return; }
    const fd = new FormData(); fd.append("title", title); fd.append("lang", lang); fd.append("file", file);
    try { await api.post(`/products/${id}/manuals`, fd); toast.success(t("adminProducts.manualOk")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const uploadManualFile = async (pid, mid, lang, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("lang", lang); fd.append("file", file);
    try { await api.post(`/products/${pid}/manuals/${mid}/file`, fd); toast.success(t("adminProducts.manualOk")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const deleteManualFile = async (pid, mid, lang) => {
    try { await api.delete(`/products/${pid}/manuals/${mid}/file/${lang}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const delManual = async (pid, mid) => {
    try { await api.delete(`/products/${pid}/manuals/${mid}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const saveManualTitle = async (pid, mid, title_i18n) => {
    try { await api.put(`/products/${pid}/manuals/${mid}`, { title_i18n }); toast.success(t("adminProducts.saved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const exportCatalog = () => {
    const cols = ["reference", "group", "name_es", "name_en", "name_de", "description_es", "specs", "manual_langs", "videos"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = products.map((p) => {
      const i = p.i18n || {};
      const manualLangs = (p.manuals || []).map((m) => (m.langs || []).join("/")).join(" | ");
      return [p.reference, p.group, i.es?.name, i.en?.name, i.de?.name, i.es?.description,
              (i.es?.specs || []).length, manualLangs, (p.videos || []).length].map(esc).join(",");
    });
    const csv = "\uFEFF" + [cols.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dosacloud-catalogo-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const ProductRow = ({ p }) => {
    const imgRef = useRef();
    const fileRef = useRef();
    const [mtitle, setMtitle] = useState("");
    const [mlang, setMlang] = useState("es");
    const [rowTr, setRowTr] = useState(normalizeTr(p.i18n));
    const [rowFeatured, setRowFeatured] = useState(!!p.featured);
    const [rowNew, setRowNew] = useState(!!p.is_new);
    const [rowGroup, setRowGroup] = useState(p.group);
    const [rowVideos, setRowVideos] = useState(p.videos || []);
    const [rowReference, setRowReference] = useState(p.reference || "");

    const saveProduct = async () => {
      try {
        await api.put(`/products/${p.id}`, { group: rowGroup, featured: rowFeatured, is_new: rowNew, reference: rowReference, videos: rowVideos.filter((v) => v.url.trim()), i18n: cleanTr(rowTr) });
        toast.success(t("adminProducts.saved"));
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e)); }
    };

    return (
      <Card className="border-slate-200 p-4 shadow-none" data-testid={`admin-product-${p.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{t(`products.group.${p.group}`)}</span>
              {p.featured && <Badge className="bg-blue-600 text-white hover:bg-blue-600"><Star className="mr-1 h-3 w-3" />{t("adminProducts.featured")}</Badge>}
              {p.is_new && <Badge className="bg-emerald-500 text-white hover:bg-emerald-500"><Sparkles className="mr-1 h-3 w-3" />{t("adminProducts.isNew")}</Badge>}
            </div>
            <h3 className="mt-1 text-sm font-semibold text-slate-800">{p.name}</h3>
            <p className="text-sm text-slate-500">{p.description}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`del-product-${p.id}`}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>

        {/* Group + flags */}
        <div className="mt-3 flex flex-wrap items-end gap-4 border-t border-slate-100 pt-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">{t("adminProducts.group")}</Label>
            <Select value={rowGroup} onValueChange={setRowGroup}>
              <SelectTrigger className="mt-1 h-9 w-48" data-testid={`row-group-${p.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{GROUPS.map((g) => <SelectItem key={g} value={g}>{t(`products.group.${g}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox checked={rowFeatured} onCheckedChange={(v) => setRowFeatured(!!v)} data-testid={`row-featured-${p.id}`} /> {t("adminProducts.featured")}
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox checked={rowNew} onCheckedChange={(v) => setRowNew(!!v)} data-testid={`row-new-${p.id}`} /> {t("adminProducts.isNew")}
          </label>
          <div className="min-w-[220px] flex-1">
            <Label className="text-xs uppercase text-slate-500">{t("adminProducts.reference")}</Label>
            <Input value={rowReference} onChange={(e) => setRowReference(e.target.value)} placeholder="DL-2000"
              className="mt-1 h-9" data-testid={`row-reference-${p.id}`} />
          </div>
        </div>

        {/* Videos */}
        <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`row-videos-${p.id}`}>
          <Label className="text-xs uppercase text-slate-500">{t("adminProducts.videos")}</Label>
          <div className="mt-1.5"><VideoEditor t={t} value={rowVideos} onChange={setRowVideos} /></div>
        </div>

        {/* Translations */}
        <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`row-tr-${p.id}`}>
          <TranslationEditor t={t} tr={rowTr} setTr={setRowTr} idPrefix={`row-${p.id}`} />
          <Button type="button" variant="secondary" size="sm" className="mt-2" onClick={saveProduct} data-testid={`save-product-${p.id}`}>
            <Save className="mr-1.5 h-4 w-4" /> {t("adminProducts.saveProduct")}
          </Button>
        </div>

        {/* Gallery */}
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Label className="text-xs uppercase text-slate-500">{t("adminProducts.images")}</Label>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {(p.images || []).map((im) => (
              <div key={im.id} className="relative h-16 w-16 overflow-hidden rounded border border-slate-200" data-testid={`admin-img-${im.id}`}>
                <img src={`${API}/api/products/${p.id}/image/${im.id}`} alt="" className="h-full w-full object-contain" />
                <button type="button" onClick={() => deleteImage(p.id, im.id)}
                  className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center bg-red-500/90 text-white" data-testid={`del-img-${im.id}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {(p.images || []).length === 0 && <span className="text-xs text-slate-400">{t("adminProducts.noImages")}</span>}
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => { uploadImage(p.id, e.target.files[0]); e.target.value = ""; }} />
            <Button variant="outline" size="sm" onClick={() => imgRef.current.click()} data-testid={`img-btn-${p.id}`}>
              <ImagePlus className="mr-1.5 h-4 w-4" /> {t("adminProducts.addImageGallery")}
            </Button>
          </div>
        </div>

        {/* Manuals */}
        <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
          {p.manuals.map((m) => (
            <ManualRow key={m.id} t={t} pid={p.id} m={m}
              onUpload={uploadManualFile} onDeleteFile={deleteManualFile} onDeleteManual={delManual} onSaveTitle={saveManualTitle} />
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input placeholder={t("adminProducts.manualTitle")} value={mtitle} onChange={(e) => setMtitle(e.target.value)}
            className="h-9 w-44" data-testid={`manual-title-${p.id}`} />
          <Select value={mlang} onValueChange={setMlang}>
            <SelectTrigger className="h-9 w-24" data-testid={`manual-lang-${p.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept="application/pdf" hidden
            onChange={(e) => { uploadManual(p.id, mtitle, mlang, e.target.files[0]); setMtitle(""); e.target.value = ""; }} />
          <Button variant="outline" size="sm" onClick={() => fileRef.current.click()} data-testid={`manual-btn-${p.id}`}>
            <Upload className="mr-1.5 h-4 w-4" /> {t("adminProducts.newManual")}
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <Layout>
      <PageHeader title={t("adminProducts.title")} subtitle={t("adminProducts.subtitle")} />
      <div className="space-y-6 p-6">
        <Tabs defaultValue="products">
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-admin-products">{t("nav.products")}</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-admin-leads">{t("adminProducts.clients")}</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-admin-downloads">{t("adminProducts.downloads")}</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-6">
            <div className="flex justify-end">
              <Button variant="outline" onClick={exportCatalog} disabled={products.length === 0} data-testid="export-catalog">
                <Download className="mr-2 h-4 w-4" /> {t("adminProducts.exportCatalog")}
              </Button>
            </div>
            <Card className="border-slate-200 p-5 shadow-none">
              <h3 className="mb-4 text-sm font-semibold text-slate-800">{t("adminProducts.newProduct")}</h3>
              <form onSubmit={createProduct} className="space-y-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="text-xs uppercase text-slate-500">{t("adminProducts.group")}</Label>
                    <Select value={group} onValueChange={setGroup}>
                      <SelectTrigger className="mt-1 h-9 w-48" data-testid="new-group"><SelectValue /></SelectTrigger>
                      <SelectContent>{GROUPS.map((g) => <SelectItem key={g} value={g}>{t(`products.group.${g}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={featured} onCheckedChange={(v) => setFeatured(!!v)} data-testid="new-featured" /> {t("adminProducts.featured")}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={isNew} onCheckedChange={(v) => setIsNew(!!v)} data-testid="new-isnew" /> {t("adminProducts.isNew")}
                  </label>
                  <div className="min-w-[200px] flex-1">
                    <Label className="text-xs uppercase text-slate-500">{t("adminProducts.reference")}</Label>
                    <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="DL-2000" className="mt-1 h-9" data-testid="new-reference" />
                  </div>
                </div>
                <div data-testid="new-translations"><TranslationEditor t={t} tr={tr} setTr={setTr} idPrefix="new" /></div>
                <div data-testid="new-videos">
                  <Label className="text-xs uppercase text-slate-500">{t("adminProducts.videos")}</Label>
                  <div className="mt-1.5"><VideoEditor t={t} value={videos} onChange={setVideos} /></div>
                </div>
                <Button type="submit" disabled={busy} data-testid="create-product">
                  <Plus className="mr-1.5 h-4 w-4" /> {t("adminProducts.create")}
                </Button>
              </form>
            </Card>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {products.map((p) => <ProductRow key={p.id} p={p} />)}
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("adminProducts.clientsList")} ({leads.length})</h3>
              </div>
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("lead.company")}</TableHead>
                    <TableHead>{t("adminProducts.interest")}</TableHead>
                    <TableHead className="text-right">{t("adminProducts.downloadsCount")}</TableHead>
                    <TableHead>{t("lead.verified")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="text-sm font-medium text-slate-800">
                        <span className="inline-flex items-center gap-1.5">
                          {l.hot && <Flame className="h-4 w-4 text-orange-500" data-testid={`lead-hot-${l.id}`} />}
                          {l.name}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{l.email}</TableCell>
                      <TableCell className="text-sm">{l.company}</TableCell>
                      <TableCell className="text-xs text-slate-500">{l.interests ? t(INTEREST_KEY[l.interests] || "") : "—"}</TableCell>
                      <TableCell className="text-right">
                        {l.hot
                          ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{l.download_count}</Badge>
                          : <span className="font-mono text-sm text-slate-500">{l.download_count}</span>}
                      </TableCell>
                      <TableCell>
                        {l.email_verified
                          ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><BadgeCheck className="h-4 w-4" /> {t("lead.verifiedYes")}</span>
                          : <span className="inline-flex items-center gap-1 text-xs text-amber-500"><Clock className="h-4 w-4" /> {t("lead.verifiedNo")}</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {leads.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400">{t("adminProducts.noClients")}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="downloads" className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card className="border-slate-200 p-5 shadow-none" data-testid="dl-total">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("adminProducts.totalDownloads")}</span>
                <div className="mt-2 font-mono text-3xl font-light text-slate-900">{downloads?.total ?? "—"}</div>
                <div className="mt-1 text-xs text-slate-400">{t("adminProducts.last90")}</div>
              </Card>
              <Card className="border-slate-200 p-5 shadow-none">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("adminProducts.topProducts")}</span>
                <div className="mt-3 space-y-2">
                  {(downloads?.top_products || []).slice(0, 6).map((p) => (
                    <div key={p.name} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{p.name}</span>
                      <span className="font-mono text-slate-400">{p.count}</span>
                    </div>
                  ))}
                  {(!downloads || downloads.top_products.length === 0) && <p className="text-sm text-slate-400">{t("adminProducts.noDownloads")}</p>}
                </div>
              </Card>
            </div>
            <Card className="border-slate-200 shadow-none">
              <div className="border-b border-slate-100 p-4">
                <h3 className="text-sm font-semibold text-slate-800">{t("adminProducts.recentDownloads")}</h3>
              </div>
              <Table data-testid="downloads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("lead.company")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("adminProducts.product")}</TableHead>
                    <TableHead>{t("adminProducts.manual")}</TableHead>
                    <TableHead>{t("analytics.date")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(downloads?.recent || []).map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-medium text-slate-800">{e.company || e.name}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{e.email}</TableCell>
                      <TableCell className="text-sm">{e.product_name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{e.manual_title}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {(downloads?.recent || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-400">{t("adminProducts.noDownloads")}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
